// 📄 components/StepAPChannels.tsx
import React from 'react';
import { ToggleLeft, ToggleRight, FileText, Truck, Receipt, FolderCheck } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepAPChannelsProps {
  apEnablePO: boolean;
  apEnableASN: boolean;
  apEnableInvoice: boolean;
  apEnableReceipt: boolean;
  onChange: (field: keyof GeneralsFormData, value: any) => void;
}

export function StepAPChannels({
  apEnablePO,
  apEnableASN,
  apEnableInvoice,
  apEnableReceipt,
  onChange
}: StepAPChannelsProps) {
  const ChannelCard = ({
    active,
    onToggle,
    icon: Icon,
    title,
    desc
  }: {
    active: boolean;
    onToggle: () => void;
    icon: any;
    title: string;
    desc: string;
  }) => (
    <div
      onClick={onToggle}
      className={`p-4 rounded-xl border cursor-pointer select-none transition-all flex items-center justify-between gap-4 ${
        active
          ? 'bg-amber-500/10 border-amber-500/30 text-white'
          : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${active ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-gray-500'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <span className="text-sm font-semibold block">{title}</span>
          <span className="text-xs text-gray-400 mt-0.5">{desc}</span>
        </div>
      </div>
      <div>
        {active ? (
          <ToggleRight className="w-8 h-8 text-amber-400" />
        ) : (
          <ToggleLeft className="w-8 h-8 text-gray-500" />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest block">SAP Ariba Buyer Portal</span>
        <p className="font-mono text-xs text-gray-300">
          <span>Ariba Administrator → Core Administration → Integration Manager → Quick Links → Document Routing</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-amber-400">2. Activación de Canales de Documento</h3>
        <p className="text-sm text-gray-400 mt-1">
          Habilita los flujos de documentos que el Ariba Buyer Portal enrutará automáticamente a través de la integración Managed Gateway (CIG).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ChannelCard
          active={apEnablePO}
          onToggle={() => onChange('apEnablePO', !apEnablePO)}
          icon={FileText}
          title="Orden de Compra (PO / ORDERS)"
          desc="Transmisión del documento de compras ERP → Ariba"
        />
        <ChannelCard
          active={apEnableASN}
          onToggle={() => onChange('apEnableASN', !apEnableASN)}
          icon={Truck}
          title="Aviso de Expedición (ASN / DESADV)"
          desc="Recepción de despachos del proveedor Ariba → ERP"
        />
        <ChannelCard
          active={apEnableReceipt}
          onToggle={() => onChange('apEnableReceipt', !apEnableReceipt)}
          icon={FolderCheck}
          title="Recepción de Mercancía (GR / RECADV)"
          desc="Sincronización de entradas de mercancía ERP → Ariba"
        />
        <ChannelCard
          active={apEnableInvoice}
          onToggle={() => onChange('apEnableInvoice', !apEnableInvoice)}
          icon={Receipt}
          title="Facturas (INVOIC)"
          desc="Ingreso de facturas comerciales Ariba → ERP"
        />
      </div>
    </div>
  );
}
